import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import SmallDetailBox from '../../components/common/DetailBox/SmallDetailBox';
import LargeDetailBox from '../../components/common/DetailBox/LargeDetailBox';
import LargeDetailTextBox from '../../components/common/DetailBox/LargeDetailTextBox';
import './CustomerDetail.css';
import './UserDetail.css';
import './PersonalProfile.css';
import apiClient, { baseurl, getMfaStatus } from '../../api';
import MfaDeviceReplaceModal from './MfaDeviceReplaceModal';
import MfaRecoveryCodesRegenerateModal from './MfaRecoveryCodesRegenerateModal';
import SelectBoxSmall from '../../components/common/SelectBox/SelectBoxSmall';
import InputBoxLarge from '../../components/common/InputBox/InputBoxLarge';
import InputBoxSmall from '../../components/common/InputBox/InputBoxSmall';
import InputAndSelectWithIconBoxSmall from '../../components/common/InputBox/InputAndSelectCompo';
import authenticatedAxios from '../../authenticatedAxios';
import { showAlert } from '../../components/common/ExportLabel/Exporterror';

const PersonalProfile = () => {
    const [userDetail, setUserDetail] = useState(null);
    const [formValues, setFormValues] = useState([]);
    const [loading, setLoading] = useState(true);
    const [countries, setCountries]= useState([]);
    const [error, setError] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [userRoles, setUserRoles] = useState([]);
    const [languages, setLanguages] = useState([]);
    const [profilePicFile, setProfilePicFile] = useState(null);
    const [profilePicPreview, setProfilePicPreview] = useState(null);
    const [mfaStatus, setMfaStatus] = useState(null);
    const [mfaLoading, setMfaLoading] = useState(true);
    const [mfaError, setMfaError] = useState(null);
    const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
    const [isRegenModalOpen, setIsRegenModalOpen] = useState(false);
    const profile_url = userDetail?.profile_pic
        ? `${baseurl.slice(0, -1)}/${userDetail.profile_pic.replace(/^\//, "")}`
        : null;
    const navigate = useNavigate();

    const fetchMfaStatus = useCallback(async () => {
        setMfaLoading(true);
        setMfaError(null);
        try {
            const data = await getMfaStatus();
            setMfaStatus(data);
        } catch (err) {
            if (err.response && err.response.status === 401) {
                // Let apiClient response interceptor handle 401 refresh or redirect
                return;
            }
            setMfaError('Failed to load Two-Factor Authentication status.');
            setMfaStatus(null);
        } finally {
            setMfaLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchRolesAndLanguages = async () => {
            try {
                const roleResponse = await authenticatedAxios.get(`${baseurl}accounts/roles/internal/`);
                setUserRoles(roleResponse.data.data || []);

                const languageResponse = await authenticatedAxios.get(`${baseurl}localization/languages/`);
                setLanguages(languageResponse.data.data || []);

                const countryResponse = await authenticatedAxios.get(`${baseurl}localization/country/`);
                setCountries(countryResponse.data.data || []);

            } catch (err) {
                setError('Failed to fetch roles or languages.');
            }
        };
        fetchRolesAndLanguages();
    },[]);

    useEffect(() => {
        fetchProfile();
        fetchMfaStatus();
    }, [fetchMfaStatus]);

    const fetchProfile = async () => {
        try {
            const response = await apiClient.get('accounts/personal-user/profile/');
            setUserDetail(response.data.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to fetch profile data.');
            setLoading(false);

            if (err.response && err.response.status === 401) {
                navigate('/login');
            }
        }
    };


    const handleUserEditClick = () => {
        setIsEdit(true);
    };

    const handleInputChange = (field, value) => {
        setFormValues(prev => ({
          ...prev,
          [field]: value,
        }));

        setUserDetail(prev => ({
            ...prev,
            [field]: value,
          }));
      
      };

    const handleProfilePicChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfilePicFile(file);
            setProfilePicPreview(URL.createObjectURL(file));
        }
    };

    const handleEditProfilePic = () => {
        document.getElementById('profile-pic-input').click();
    };


    const handleUserUpdate = async () => {
        if (!profilePicFile && Object.keys(formValues).length === 0) {
            setIsEdit(false);
            setError(null);
            return;
        }
    
        try {
            let dataToSend = formValues;
    
            // If a file is present, use FormData
            if (profilePicFile) {
                const formData = new FormData();
                formData.append('profile_pic', profilePicFile);
    
                for (const key in formValues) {
                    formData.append(key, formValues[key]);
                }
    
                dataToSend = formData;
            }
    
            const response = await apiClient.put('accounts/personal-user/profile/', dataToSend, {
                headers: profilePicFile ? { 'Content-Type': 'multipart/form-data' } : {},
            });
    
            if (response.data.response_code === 1) {
                fetchProfile(); // Refresh latest profile data
                setIsEdit(false);
                setFormValues([]);
                setProfilePicPreview(null);
                setError(null);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Something went wrong!';
            showAlert(errorMessage);
        }
    };
    
    

    const formatDateTime = (isoString) => {
        if (!isoString) return 'N/A';
        try {
            const date = new Date(isoString);
            if (isNaN(date.getTime())) return 'N/A';
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return 'N/A';
        }
    };

    const renderMfaSecurityCard = () => (
        <div className="mfa-security-card" data-testid="mfa-security-card">
            <div className="mfa-card-header">
                <div className="mfa-header-text">
                    <span className="common-text fts-17 ftw-6 black d-block">Two-Factor Authentication (MFA)</span>
                    <span className="common-text fts-13 dk-gray">Google Authenticator / TOTP Security</span>
                </div>
                <div>
                    {mfaLoading ? (
                        <span className="mfa-status-badge mfa-badge-loading" data-testid="mfa-badge-loading">
                            Checking...
                        </span>
                    ) : mfaError ? (
                        <span className="mfa-status-badge mfa-badge-error" data-testid="mfa-badge-error">
                            Unavailable
                        </span>
                    ) : mfaStatus?.is_enrolled || mfaStatus?.has_active_device ? (
                        <span className="mfa-status-badge mfa-badge-enabled" data-testid="mfa-badge-enabled">
                            ● Enabled
                        </span>
                    ) : (
                        <span className="mfa-status-badge mfa-badge-disabled" data-testid="mfa-badge-disabled">
                            ● Not Enabled
                        </span>
                    )}
                </div>
            </div>

            <div className="mfa-card-content mt-15">
                {mfaLoading ? (
                    <div className="mfa-state-container" data-testid="mfa-loading-container">
                        <div className="mfa-loading-spinner" />
                        <span className="common-text fts-14 dk-gray mt-10">Loading Two-Factor Authentication status...</span>
                    </div>
                ) : mfaError ? (
                    <div className="mfa-state-container mfa-error-container" data-testid="mfa-error-container" role="alert">
                        <span className="common-text fts-14 red mb-10">{mfaError}</span>
                        <button
                            type="button"
                            className="mfa-retry-btn common-text fts-13"
                            onClick={fetchMfaStatus}
                            data-testid="mfa-retry-btn"
                        >
                            Retry
                        </button>
                    </div>
                ) : mfaStatus?.is_enrolled || mfaStatus?.has_active_device ? (
                    <div className="mfa-details-grid" data-testid="mfa-details-grid">
                        <div className="mfa-info-box">
                            <span className="mfa-box-label fts-12 lt-gray">Authentication Method</span>
                            <span className="mfa-box-value fts-15 ftw-5 black">Authenticator App (TOTP)</span>
                        </div>
                        <div className="mfa-info-box">
                            <span className="mfa-box-label fts-12 lt-gray">Remaining Recovery Codes</span>
                            <span className="mfa-box-value fts-15 ftw-5 black" data-testid="mfa-recovery-codes-count">
                                {mfaStatus.remaining_recovery_codes ?? 0} active codes
                            </span>
                        </div>
                        <div className="mfa-info-box">
                            <span className="mfa-box-label fts-12 lt-gray">Enrolled On</span>
                            <span className="mfa-box-value fts-14 ftw-4 black" data-testid="mfa-enrolled-at">
                                {formatDateTime(mfaStatus.enrolled_at)}
                            </span>
                        </div>
                        <div className="mfa-info-box">
                            <span className="mfa-box-label fts-12 lt-gray">Last Device Verification</span>
                            <span className="mfa-box-value fts-14 ftw-4 black" data-testid="mfa-last-used-at">
                                {formatDateTime(mfaStatus.last_used_at)}
                            </span>
                        </div>

                        {Number(mfaStatus.remaining_recovery_codes) <= 3 && (
                            <div className="mfa-warning-alert mt-10" data-testid="mfa-low-codes-warning">
                                ⚠️ You have few recovery codes remaining. Consider regenerating them once available.
                            </div>
                        )}

                        <div className="mfa-info-note mt-15">
                            <p className="common-text fts-12 dk-gray m-0">
                                Two-factor authentication is active on your Platform SuperAdmin account.
                                Each login requires your password and a 6-digit verification code.
                            </p>
                        </div>

                        <div className="mfa-placeholder-actions mt-20">
                            <button
                                type="button"
                                className="mfa-action-btn-active"
                                onClick={() => setIsReplaceModalOpen(true)}
                                data-testid="mfa-start-replace-btn"
                            >
                                Replace Authenticator App
                            </button>
                            <button
                                type="button"
                                className="mfa-action-btn-secondary"
                                onClick={() => setIsRegenModalOpen(true)}
                                data-testid="mfa-start-regen-btn"
                            >
                                Regenerate Recovery Codes
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="mfa-not-enrolled-box" data-testid="mfa-not-enrolled">
                        <p className="common-text fts-14 dk-gray">
                            Two-factor authentication is currently <strong>Not Enabled</strong>.
                        </p>
                        <p className="common-text fts-12 dk-gray mt-5">
                            As a Platform SuperAdmin, you will be prompted to set up an authenticator app upon your next login attempt.
                        </p>
                    </div>
                )}
            </div>

            <MfaDeviceReplaceModal
                isOpen={isReplaceModalOpen}
                onClose={() => setIsReplaceModalOpen(false)}
                onSuccess={() => {
                    setIsReplaceModalOpen(false);
                    fetchMfaStatus();
                }}
            />

            <MfaRecoveryCodesRegenerateModal
                isOpen={isRegenModalOpen}
                onClose={() => setIsRegenModalOpen(false)}
                onSuccess={() => {
                    setIsRegenModalOpen(false);
                    fetchMfaStatus();
                }}
            />
        </div>
    );

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="whole-container">
            <div className="top-heading">
                <PageHeader pageName="My Profile" />
            </div>

            {!isEdit ? (
                <div className="User-detail-container detail-container personal-profile-container">
                    <div className="my-profile-whole left-user-details-whole">
                        <div className="my-profile-top bg-blue company-left-first left-customer">
                            <div className="company-img-name-container">
                                <div className="company-img-container">
                                    <img
                                        src={profilePicPreview || `${profile_url}`}
                                        alt="Profile"
                                        className="comp-img user-image"
                                    />
                                </div>
                                <div>
                                    <span className="common-text fts-17 white">{userDetail.full_name || 'N/A'}</span>
                                    <span className="common-text fts-13 white">{userDetail.email || 'N/A'}</span>
                                </div>
                            </div>
                            <div className="company-edit-button">
                                <button className="fts-15 black" onClick={handleUserEditClick}>
                                    Edit
                                </button>
                            </div>
                        </div>
                        <div className="company-left-second left-customer">
                          <LargeDetailBox label="Name" value={userDetail.full_name || 'N/A'} />
                          <SmallDetailBox label="Email" value={userDetail.email|| 'N/A'} />
                          <SmallDetailBox
                                label="Mobile Number"
                                value={`${userDetail.country_code?.country_code || ''} ${userDetail.phone || 'N/A'}`}
                            />

                          <SmallDetailBox label="User Role" value={userDetail.role?.name || 'N/A'} />
                          <SmallDetailBox label="User Type" value={userDetail.user_type || 'N/A'} />

                          <SmallDetailBox label="Preferred Language" value={userDetail.preferred_language?.name || 'N/A'} />
                          <SmallDetailBox label="User Time Zone" value={userDetail.time_zone || 'N/A'} />
                        </div>
                    </div>

                    <div className="my-profile-whole right-user-details-whole mfa-security-card-whole">
                        {renderMfaSecurityCard()}
                    </div>
                </div>
            ) : (
                <div className="User-detail-container edit-container personal-profile-container">
                    <div className="my-profile-whole left-user-details-whole">
                        <div className="my-profile-top bg-blue company-left-first left-customer">
                            <div className="company-img-name-container">
                                <div className="company-img-container">
                                    <img
                                        src={profilePicPreview || `${profile_url}`}
                                        alt="Profile Preview"
                                        className="comp-img user-image"
                                    />
                                    <img
                                        src="/assets/company_edit.png"
                                        alt="edit"
                                        className="edit-company"
                                        onClick={handleEditProfilePic}
                                    />
                                    <input
                                        id="profile-pic-input"
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleProfilePicChange}
                                    />
                                </div>
                                <div>
                                    <span className="common-text fts-17 white">{userDetail.full_name || 'N/A'}</span>
                                    <span className="common-text fts-13 white">{userDetail.email || 'N/A'}</span>
                                </div>
                            </div>
                            <div className="company-edit-button">
                                <button className="fts-15 black" onClick={handleUserUpdate}>
                                    Save
                                </button>
                            </div>
                        </div>
                        <div className=" left-customer">
                            <InputBoxLarge
                            label="Name"
                            border='blue-border'
                            defaultValue={userDetail.full_name}
                            onChange={(e) => handleInputChange("full_name", e.target.value)}
                            />
                            <div className='user-edit-box'>
                                <InputBoxSmall
                                label="Email"
                                className='w-50'
                                border='blue-border'
                                defaultValue={userDetail.email}
                                onChange={(e) => handleInputChange("email", e.target.value)}
                                />

                                <InputAndSelectWithIconBoxSmall
                                    label="Mobile Number"
                                    name="phone"
                                    className='w-50'
                                    opt="country_code"
                                    border='blue-border'
                                    options={countries.map(c => ({
                                        value: c.id.toString(),
                                        label: c.country_code
                                    }))}
                                    onChange={(e) => handleInputChange("phone", e.target.value)}
                                    selectchange={(e) => handleInputChange("country_code", e.target.value)}
                                    defaultValue={userDetail?.phone || ''}
                                    defaultOption={userDetail?.country_code?.id}
                                />
                            </div>
                            <div className='user-edit-box'>
                                <SelectBoxSmall
                                    label="User Role"
                                    className='w-50'
                                    border='blue-border'
                                    options={userRoles.map((role) => ({ value: role.id, label: role.name }))}
                                    defaultValue={userDetail?.role?.id || ''}
                                    onChange={(e) => handleInputChange("role", e.target.value)}
                                />
                                <SelectBoxSmall
                                    label="User Type"
                                    className='w-50'
                                    border='blue-border'
                                    options={[
                                        { value: 'Vansaler', label: 'Vansaler' },
                                        { value: 'Employee', label: 'Employee' },
                                        { value: 'Both', label: 'Both' },
                                    ]}
                                    defaultValue={userDetail?.user_type || ''}
                                    onChange={(e) => handleInputChange("user_type", e.target.value)}
                                />
                            </div>
                            <div className='user-edit-box'>
                                
                                 <SelectBoxSmall
                                    label="Preferred Language"
                                    className='w-50'
                                    border='blue-border'
                                    options={languages.map((language) => ({ value: language.id, label: language.name }))}
                                    defaultValue={userDetail?.preferred_language?.id || ''}
                                    onChange={(e) => handleInputChange("preferred_language", e.target.value)}
                                />
                                <InputBoxSmall
                                    label="User Timezone"
                                    className='w-50'
                                    border='blue-border'
                                    readOnly
                                    defaultValue='Coming Soon'
                                    // options={[
                                    //     { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
                                    //     { value: 'America/New_York', label: 'America/New_York (EST)' },
                                    // ]}
                                    // defaultValue={userDetail?.time_zone || ''}
                                    // onChange={(e) => setUserDetail({ ...userDetail, time_zone: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="my-profile-whole right-user-details-whole mfa-security-card-whole">
                        {renderMfaSecurityCard()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonalProfile;

